import { JALALI_MONTHS, toJalali, zonedParts } from './jalali'

const PERSIAN_DIGITS = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹']
export const DECIMAL_MARK = '٫'
export const THOUSANDS_MARK = ','
export const PERCENT_MARK = '٪'

/** What a missing number renders as. */
export const EMPTY_VALUE = '-'

/** Latin digits to Persian digits. Leaves everything else alone. */
export function toPersianDigits(input: string | number): string {
  return String(input).replace(/[0-9]/g, (d) => PERSIAN_DIGITS[Number(d)])
}

/** Persian or Arabic digits to Latin digits, for parsing user input. */
export function toLatinDigits(input: string): string {
  return input
    .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06f0))
    .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660))
    .replace(/٫/g, '.')
    .replace(/٬/g, '')
}

export interface NumberOptions {
  /** Fixed number of decimals. Omitted means "as many as the value has, max 2". */
  decimals?: number
  /** Group thousands. Default true. */
  group?: boolean
  /** Render with Latin digits, for inputs and machine contexts. */
  latin?: boolean
}

/** A number as the product writes it. */
export function formatNumber(value: number | null | undefined, opts: NumberOptions = {}): string {
  if (value === null || value === undefined || Number.isNaN(value)) return EMPTY_VALUE
  const { decimals, group = true, latin = false } = opts
  const abs = Math.abs(value)
  const fixed = decimals !== undefined ? abs.toFixed(decimals) : trimDecimals(abs)
  const [intPart, fracPart] = fixed.split('.')
  const grouped = group ? intPart.replace(/\B(?=(\d{3})+(?!\d))/g, THOUSANDS_MARK) : intPart
  let out = grouped
  if (fracPart) out += (latin ? '.' : DECIMAL_MARK) + fracPart
  if (value < 0) out = '-' + out
  return latin ? out : toPersianDigits(out)
}

function trimDecimals(n: number): string {
  if (Number.isInteger(n)) return String(n)
  return String(Math.round(n * 100) / 100)
}

/** A Rial amount. The unit is rendered by the caller, usually as a .unit span. */
export function formatRial(value: number | null | undefined): string {
  return formatNumber(value, { decimals: 0 })
}

/** A weight in kilograms. */
export function formatWeight(value: number | null | undefined): string {
  return formatNumber(value, { decimals: 0 })
}

/** A percentage, sign first in logical order so bidi renders it correctly. */
export function formatPercent(value: number | null | undefined, decimals = 1): string {
  if (value === null || value === undefined || Number.isNaN(value)) return EMPTY_VALUE
  return formatNumber(Math.abs(value), { decimals }) + PERCENT_MARK
}

/** A signed delta, as the ticker writes it: ۲٫۴٪ + */
export function formatDelta(value: number | null | undefined, decimals = 1): string {
  if (value === null || value === undefined || Number.isNaN(value)) return EMPTY_VALUE
  const sign = value > 0 ? '+' : value < 0 ? '-' : ''
  return `${formatPercent(value, decimals)} ${sign}`.trimEnd()
}

/** ۱۴۰۵/۰۶/۱۳, or 1405/06/13 with `latin`, which is what a date field carries. */
export function formatJalaliDate(
  date: Date | string | number | null | undefined,
  opts: { latin?: boolean } = {},
): string {
  const d = asDate(date)
  if (!d) return opts.latin ? '' : EMPTY_VALUE
  const { jy, jm, jd } = toJalali(d)
  const out = `${jy}/${pad(jm)}/${pad(jd)}`
  return opts.latin ? out : toPersianDigits(out)
}

/** ۱۳ شهریور ۱۴۰۵ */
export function formatJalaliLong(date: Date | string | number | null | undefined): string {
  const d = asDate(date)
  if (!d) return EMPTY_VALUE
  const { jy, jm, jd } = toJalali(d)
  return `${toPersianDigits(jd)} ${JALALI_MONTHS[jm - 1]} ${toPersianDigits(jy)}`
}

/** ۱۰:۰۵ or ۱۰:۰۵:۳۰ */
export function formatTime(
  date: Date | string | number | null | undefined,
  withSeconds = false,
): string {
  const d = asDate(date)
  if (!d) return EMPTY_VALUE
  const p = zonedParts(d)
  const base = `${pad(p.hour)}:${pad(p.minute)}`
  return toPersianDigits(withSeconds ? `${base}:${pad(p.second)}` : base)
}

/** ۱۴۰۵/۰۶/۱۳ ساعت ۱۰:۰۵ */
export function formatDateTime(date: Date | string | number | null | undefined): string {
  const d = asDate(date)
  if (!d) return EMPTY_VALUE
  return `${formatJalaliDate(d)} ساعت ${formatTime(d)}`
}

/** A duration in seconds as ۰۲:۱۴:۳۰, the validity clock format. */
export function formatCountdown(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds))
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const sec = s % 60
  return toPersianDigits(`${pad(h)}:${pad(m)}:${pad(sec)}`)
}

/** Remaining time in words, for feeds and notification lists. */
export function formatRelative(date: Date | string | number | null | undefined, now = new Date()): string {
  const d = asDate(date)
  if (!d) return EMPTY_VALUE
  const diff = Math.round((now.getTime() - d.getTime()) / 1000)
  const abs = Math.abs(diff)
  const past = diff >= 0
  const unit = (n: number, word: string) =>
    past ? `${toPersianDigits(n)} ${word} پیش` : `${toPersianDigits(n)} ${word} دیگر`
  if (abs < 60) return past ? 'هم‌اکنون' : 'تا لحظاتی دیگر'
  if (abs < 3600) return unit(Math.floor(abs / 60), 'دقیقه')
  if (abs < 86400) return unit(Math.floor(abs / 3600), 'ساعت')
  if (abs < 86400 * 30) return unit(Math.floor(abs / 86400), 'روز')
  return formatJalaliDate(d)
}

/** A national id or registration number, grouped for reading. */
export function formatNationalId(value: string): string {
  return toPersianDigits(toLatinDigits(value))
}

/** A mobile number as ۰۹۱۲ ۳۴۵ ۶۷۸۹ */
export function formatMobile(value: string): string {
  const raw = toLatinDigits(value).replace(/\D/g, '')
  const m = raw.match(/^(\d{4})(\d{3})(\d{4})$/)
  return toPersianDigits(m ? `${m[1]} ${m[2]} ${m[3]}` : raw)
}

/** Masked mobile for confirmation screens: ۰۹۱۲ ••• ۶۷۸۹ */
export function maskMobile(value: string): string {
  const raw = toLatinDigits(value).replace(/\D/g, '')
  const m = raw.match(/^(\d{4})(\d{3})(\d{4})$/)
  if (!m) return toPersianDigits(raw)
  return `${toPersianDigits(m[1])} ••• ${toPersianDigits(m[3])}`
}

/** Parse a user typed number that may carry Persian digits and separators. */
export function parseNumber(input: string): number | null {
  const cleaned = toLatinDigits(input).replace(/[,\s٬]/g, '')
  if (cleaned === '' || cleaned === '-') return null
  const n = Number(cleaned)
  return Number.isFinite(n) ? n : null
}

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n)
}

function asDate(v: Date | string | number | null | undefined): Date | null {
  if (v === null || v === undefined) return null
  const d = v instanceof Date ? v : new Date(v)
  return Number.isNaN(d.getTime()) ? null : d
}
